import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Loader2, Disc3, ChevronRight, Gauge } from "lucide-react";
import api from "../lib/apiClient";
import { RISK_META, fmtDate } from "../lib/gameData";
import { toast } from "sonner";

const TYPES = ["Single", "EP", "Album", "Clip", "Autre"];
const STATES = ["idée", "morceau en cours", "morceau terminé", "mix terminé", "master terminé", "cover terminée", "contenu en préparation", "presque prêt", "prêt à sortir"];
const OBJECTIVES = ["sortir le morceau", "développer mon audience", "augmenter mes streams", "obtenir plus de visibilité", "préparer un lancement professionnel", "tester une nouvelle stratégie", "autre"];

export default function Releases() {
  const navigate = useNavigate();
  const [rels, setRels] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/releases").then((r) => setRels(r.data));
  useEffect(() => { load(); }, []);

  if (!rels) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><Disc3 className="w-7 h-7 text-amber-400" /> Mes sorties</h1>
          <p className="text-zinc-500 text-sm mt-1">Planifie, prépare et pilote tes sorties musicales.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-release-btn" className="shrink-0 inline-flex items-center gap-2 px-4 h-11 rounded-xl gold-gradient text-black font-semibold hover:opacity-90"><Plus className="w-5 h-5" /> Nouvelle sortie</button>
      </div>

      {rels.length === 0 ? (
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-10 text-center" data-testid="releases-empty">
          <Disc3 className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400">Aucune sortie pour l'instant. Prépare ta prochaine sortie !</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {rels.map((r) => (
            <motion.button key={r.id} onClick={() => navigate(`/releases/${r.id}`)} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="text-left bg-[#18181E] border border-zinc-800 rounded-3xl p-5 card-hover" data-testid={`release-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">{r.type}</span>
                <span className="text-xs" style={{ color: RISK_META[r.risk].color }}>{RISK_META[r.risk].dot} {RISK_META[r.risk].label}</span>
              </div>
              <h3 className="font-display text-xl font-bold mt-3">{r.title}</h3>
              <p className="text-sm text-zinc-500 mt-1">{r.days_remaining >= 0 ? `Sortie dans ${r.days_remaining} j` : `Sortie il y a ${-r.days_remaining} j`} · {fmtDate(r.target_date)}</p>
              <div className="mt-3 flex items-center gap-2">
                <Gauge className="w-4 h-4 text-amber-400" />
                <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full gold-gradient" style={{ width: `${r.readiness}%` }} /></div>
                <span className="font-mono text-sm">{r.readiness}</span>
              </div>
              {r.next_action && <p className="text-xs text-zinc-400 mt-3 flex items-center gap-1"><ChevronRight className="w-3 h-3" /> {r.next_action.title}</p>}
            </motion.button>
          ))}
        </div>
      )}

      {open && <Wizard onClose={() => setOpen(false)} onCreated={(id) => { setOpen(false); navigate(`/releases/${id}`); }} />}
    </div>
  );
}

function Wizard({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ title: "", type: "Single", objective: OBJECTIVES[0], target_date: "", current_state: "idée", generate_plan: true });
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!f.title.trim() || !f.target_date) { toast.error("Titre et date cible requis"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/releases", f);
      toast.success("Sortie créée 🎵");
      onCreated(data.id);
    } catch (e) { toast.error("Erreur"); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="release-wizard">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
        className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6 w-full max-w-lg">
        <p className="text-xs font-mono text-amber-500 mb-1">Étape {step}/6</p>
        {step === 1 && <Wrap title="Que veux-tu sortir ?"><Grid opts={TYPES} value={f.type} onChange={set("type")} test="wiz-type" /></Wrap>}
        {step === 2 && <Wrap title="Quel est ton objectif ?"><Sel opts={OBJECTIVES} value={f.objective} onChange={set("objective")} test="wiz-objective" /><Inp label="Titre de la sortie" value={f.title} onChange={set("title")} test="wiz-title" placeholder="Ex : Mon nouveau single" /></Wrap>}
        {step === 3 && <Wrap title="Quand veux-tu sortir ?"><Inp type="date" label="Date cible (ton fuseau)" value={f.target_date} onChange={set("target_date")} test="wiz-date" /></Wrap>}
        {step === 4 && <Wrap title="État actuel du projet ?"><Sel opts={STATES} value={f.current_state} onChange={set("current_state")} test="wiz-state" /></Wrap>}
        {step === 5 && <Wrap title="Disponibilité"><p className="text-sm text-zinc-400">Cehnura utilisera tes disponibilités enregistrées dans ton profil pour éviter un planning irréaliste.</p></Wrap>}
        {step === 6 && <Wrap title="Plan recommandé ?"><Grid opts={["Oui, générer le plan", "Non, créer manuellement"]} value={f.generate_plan ? "Oui, générer le plan" : "Non, créer manuellement"} onChange={(v) => set("generate_plan")(v.startsWith("Oui"))} test="wiz-plan" /></Wrap>}
        <div className="flex justify-between gap-2 mt-6">
          <button onClick={step === 1 ? onClose : () => setStep(step - 1)} className="px-4 h-11 rounded-xl border border-zinc-700 text-zinc-300">{step === 1 ? "Annuler" : "Retour"}</button>
          {step < 6 ? (
            <button onClick={() => setStep(step + 1)} data-testid="wiz-next" className="px-6 h-11 rounded-xl gold-gradient text-black font-semibold">Suivant</button>
          ) : (
            <button onClick={submit} disabled={loading} data-testid="wiz-submit" className="px-6 h-11 rounded-xl gold-gradient text-black font-semibold flex items-center gap-2 disabled:opacity-60">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Créer la sortie"}</button>
          )}
        </div>
      </motion.div>
    </div>
  );
}

const Wrap = ({ title, children }) => <div><h3 className="font-display text-xl font-bold mb-4">{title}</h3><div className="space-y-3">{children}</div></div>;
const Grid = ({ opts, value, onChange, test }) => (
  <div className="grid grid-cols-2 gap-2" data-testid={test}>
    {opts.map((o) => <button key={o} onClick={() => onChange(o)} className={`px-3 py-3 rounded-xl border text-sm ${value === o ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-zinc-800 text-zinc-300"}`}>{o}</button>)}
  </div>
);
const Sel = ({ opts, value, onChange, test }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={test} className="w-full h-11 px-3 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 outline-none focus:border-amber-500">
    {opts.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);
const Inp = ({ label, value, onChange, test, type = "text", placeholder }) => (
  <div><label className="text-xs text-zinc-500 mb-1 block">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={test} placeholder={placeholder}
      className="w-full h-11 px-3 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 outline-none focus:border-amber-500" /></div>
);
